import React, { useEffect, useLayoutEffect } from 'react';
import { Router } from 'react-router-dom';
import { dispatch, useReframe } from 'nike-re-framejs';
import { NikeContext } from 'nike-ui/src/common/NikeContext';

import BetslipPopups from '@/ui/views/BetslipPopup';
import MainSwitch from '@/routing/MainSwitch';
import { ErrorBoundaryX } from '@/common/components/error/ErrorBoundary';
import routingHistory from '@/routing/history';
import { hasQueryParam } from '@/routing/QueryParamRoute';
import LoginOverlay from '@/loginng/views/LoginDialog';
import { ResponsibleGaming } from '@/common/components/RealityCheck';
import MainTabBar from '@/menu/views/MainTabBar';
import TopHeader from '@/header/TopHeader';
import { Footer } from '@/common/components/footer';
import { isLoggedIn } from '@/account';
import PopupPortal from '@/ui/PopupPortal';
import BetslipRoot from '@/betslip/views/BetslipRoot';
import PageInterceptor from '@/routing/PageInterceptor';
import ActiveTicketsLoader from '@/routing/ActiveTicketsLoader';
import { measureTime } from '@/analytics';
import { DebugConsole, hideDebugConsole, showDebugConsole } from 'nike-ui/src/common/logging/console/DebugConsole';
import { fullPageClassnames, pageClassnames } from '@/root/pageClassnames';
import NotificationBox from '@/notification-popup/views/NotificationBox';
import { accountForcedActionsCrm$ } from '@/account/subs';
import PopupNg from '@/ui/PopupNg';
import useSessionStorage from '@/_hooks/useSessionStorage';
import MobileViewCashinClosingModeActive from '@/mytickets/components/ticket-closing-mode-editor/MobileViewCashinClosingModeActive';
import { cashinClosingModeAnyActive$ } from 'nike-ui/src/components/ticketus/subs';
import { isProfileBookingLandscapeEnabled, isProfileOlympiaEnabled } from 'nike-ui/src/profiles';
import useLandscape from 'nike-ui/src/hooks/useLandscape';
import OnScrollPage from '@/common/components/onScrollPage/OnScrollPage';
import LuckyWheelNotificationToast from 'nike-ui/src/components/lucky-wheel/LuckyWheelNotificationToast';
import { openLuckyWheelModal } from '@/luckyWheel/h&s';
import { trackDwhPageViewEvent } from 'nike-ui/src/components/dwh-tracking/api';
import modules from '@/markdown/modules';
import MarkdownContext from 'nike-ui/src/components/markdown/MarkdownContext';

function NikeRoot() {
    useLayoutEffect(() => {
        measureTime('NikeRoot');
    }, []);
    const landscape = useLandscape() && isProfileBookingLandscapeEnabled();
    const { subscribe } = useReframe();
    const cashinClosingModeActive = subscribe(cashinClosingModeAnyActive$);
    const forcedActionsCrm = subscribe(accountForcedActionsCrm$);
    const [shouldOpenDataChangeVerification, setOpenDataChangeVerification] = useSessionStorage(
        'data-change-verification',
        true
    );

    useEffect(() => {
        trackDwhPageViewEvent();
    }, []);

    useEffect(() => {
        if (forcedActionsCrm?.length && shouldOpenDataChangeVerification) {
            PopupNg.dispatcher.open('data-change-verification', { forcedActionsCrm });
            setOpenDataChangeVerification(false);
        }
    }, [forcedActionsCrm, setOpenDataChangeVerification, shouldOpenDataChangeVerification]);

    return (
        <NikeContext>
            <MarkdownContext.Provider value={{ modules }}>
                <div className="onPhone theme-light">
                    <DebugConsole />
                    <ErrorBoundaryX description={'Root'}>
                        <BetslipPopups />
                        <PopupPortal />
                        <Router history={routingHistory}>
                            <MainSwitch
                                render={(route, match, location, history) => {
                                    if (location.hash === '#console') {
                                        showDebugConsole();
                                    }
                                    if (location.hash === '#hideconsole') {
                                        hideDebugConsole();
                                    }
                                    const PageComponent = route.component;
                                    const ticketEditorOpened = hasQueryParam(location.search, 'tiket');
                                    return (
                                        <div className={fullPageClassnames(route)}>
                                            <PageInterceptor
                                                route={route}
                                                match={match}
                                                location={location}
                                                history={history}
                                            />
                                            <main id="page" role="main">
                                                <TopHeader route={route} />
                                                <div className={pageClassnames(route)}>
                                                    <PageComponent route={route} {...match.params} />
                                                </div>
                                                <Footer route={route} closingModePadding={cashinClosingModeActive} />
                                            </main>
                                            {hasQueryParam(location, 'prihlasenie') && !isLoggedIn() ? (
                                                <LoginOverlay />
                                            ) : null}
                                            {landscape && <OnScrollPage />}
                                            <BetslipRoot route={route} ticketEditorOpened={ticketEditorOpened} />
                                            {cashinClosingModeActive && <MobileViewCashinClosingModeActive />}
                                            {!ticketEditorOpened && <MainTabBar route={route} landscape={landscape} />}
                                            <ActiveTicketsLoader />
                                            <ResponsibleGaming />
                                            {isLoggedIn() && isProfileOlympiaEnabled() ? <NotificationBox /> : null}
                                            {isLoggedIn() && (
                                                <LuckyWheelNotificationToast
                                                    onNotificationClick={() => dispatch(openLuckyWheelModal)}
                                                />
                                            )}
                                        </div>
                                    );
                                }}
                            />
                        </Router>

                        <div id="chat-message-ticket-detail-popup" />
                    </ErrorBoundaryX>
                </div>
            </MarkdownContext.Provider>
        </NikeContext>
    );
}

export default NikeRoot;
